import React, {useEffect, useState} from 'react';
import {Card, CardContent, Box, Link, Typography} from '@mui/material';
import {ChevronDownDark, ChevronUpDark} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {useInView, useIsCustomWidth} from '@jobseeker/lotus/hooks';
import {BottomSheet} from '@jobseeker/lotus/elements';
import {highLights} from '../type';
import {getKeyFromPath, TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {AppState, JobDetail} from '@jobseeker/common-core/types';
import {useSelector} from 'react-redux';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const CompanyHighLights: React.FC<{
  highLights: highLights[];
  heading: string;
  templateInfo?: {
    templateNo?: string;
    origin?: string;
  };
  isExpanded?: boolean;
  onToggle?: () => void;
}> = ({highLights, heading, templateInfo, isExpanded = false, onToggle}) => {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const jobInfo: JobDetail = useSelector((state: AppState) => state.jobDetail);

  const showAll = isExpanded;
  const toggleShowAll = onToggle || (() => {});
  const {ref, isInView} = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const [companyHighlightsTracked, setCompanyHighlightsTracked] =
    useState(false);

  useEffect(() => {
    if (isInView && !companyHighlightsTracked) {
      TrackEventUBA('widgetView', 'view', {
        ref: router.query.ref || '',
        pageName: 'JD',
        jobID: jobInfo.id,
        page: 'JD',
        templateID: templateInfo?.templateNo,
        isBranded: 'Yes',
        companyID: jobInfo?.companyData?.companyId,
        centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
        companyName: jobInfo?.companyData?.companyName,
        section: 'KeyHighlights',
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
      setCompanyHighlightsTracked(true);
    }
  }, [isInView, companyHighlightsTracked]);
  const isSmall = useIsCustomWidth('sm');

  const showHighLights = (show?: boolean) => {
    const vis = isSmall ? 2 : 3;

    const displayedBenefits =
      showAll || show ? highLights : highLights?.slice(0, vis);

    return (
      <Box p={2} pb={0.5} px={1.5} ref={ref}>
        <Card
          variant="outlined"
          sx={{
            border: 'none',
            backgroundColor: open ? '#fff' : '#F9FAFB',
          }}
        >
          <CardContent
            sx={{
              padding: 0,
              '&:last-child': {
                pb: 0,
              },
            }}
          >
            {displayedBenefits.map((b, idx) => (
              <Box
                key={idx}
                display={'flex'}
                alignItems={'start'}
                mb={{xxs: 2, md: 2}}
              >
                <Image
                  src={b?.keyHighlightsIcon || ''}
                  height={32}
                  width={32}
                  alt="icon"
                />
                <Box ml={2} mt={isAppIimjobs ? 0 : -0.25}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    lineHeight={'16px'}
                  >
                    {b.keyHighlightsSubheading}
                  </Typography>
                  <Typography
                    fontSize={12}
                    fontWeight={300}
                    mt={'2px'}
                    color={isAppIimjobs ? '#7E7E7E' : '#000'}
                    lineHeight={'16px'}
                  >
                    {b.keyHighlightsDescription}
                  </Typography>
                </Box>
              </Box>
            ))}
            {highLights.length > vis && !show && (
              <Box
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => {
                  TrackEventUBA('jdPageClick', 'click', {
                    ref: router.query.ref || '',
                    pageName: 'Job Description Page',
                    jobID: jobInfo?.id,
                    jobVersion: '',
                    jobRef: getKeyFromPath('ref') || '',
                    jobPos: router.query.jobPos || '',
                    page: 'JD',
                    section: 'KeyHighlights',
                    isBranded: 'Yes',
                    sessionUsername: sessionStorage.getItem('userId') || '',
                    typeClick: `KeyHighlights-${
                      showAll ? 'ShowLess' : 'ShowMore'
                    }`,
                    companyID: jobInfo?.companyData?.companyId,
                    centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
                    companyName: jobInfo?.companyData?.companyName,
                    templateID: templateInfo?.templateNo,
                  });
                  if (isSmall) {
                    setOpen(true);
                  } else {
                    toggleShowAll();
                  }
                }}
                sx={{cursor: 'pointer', pb: 1.5}}
              >
                <Link
                  color="primary"
                  underline="none"
                  letterSpacing="0.48px"
                  lineHeight="18px"
                  fontSize={isAppIimjobs ? 12 : 14}
                  fontWeight={isAppIimjobs ? 500 : 600}
                >
                  {showAll ? 'Show Less' : 'Show More'}
                </Link>
                {!isSmall && (
                  <Image
                    src={!showAll ? ChevronDownDark : ChevronUpDark}
                    alt="chevron"
                    height={18}
                    width={18}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <>
      {' '}
      <BottomSheet
        maxHeight={600}
        open={open}
        title={heading}
        onDismiss={() => {
          setOpen(false);
          TrackEventUBA('jdPageClick', 'click', {
            ref: router.query.ref || '',
            pageName: 'Job Description Page',
            jobID: jobInfo?.id,
            jobVersion: '',
            jobRef: getKeyFromPath('ref') || '',
            jobPos: router.query.jobPos || '',
            page: 'JD',
            section: 'KeyHighlights',
            isBranded: 'Yes',
            sessionUsername: sessionStorage.getItem('userId') || '',
            typeClick: 'KeyHighlights-ShowLess',
            companyID: jobInfo?.companyData?.companyId,
            centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
            companyName: jobInfo?.companyData?.companyName,
            templateID: templateInfo?.templateNo,
          });
        }}
      >
        {showHighLights(true)}
      </BottomSheet>
      {showHighLights()}
    </>
  );
};

export default CompanyHighLights;
